import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar as CalendarIcon, Search, Filter, Eye, Download } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { formatDateBR } from '@/utils/dateUtils';
import api from '@/lib/api';
import { DisparoLote } from '@/contexts/DisparosContext';
import ExportarLotesModal from '@/components/modals/ExportarLotesModal';

interface HistoricoLotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onViewDetails: (lote: DisparoLote) => void;
  user: any;
  clienteId: number | null;
}

const HistoricoLotesModal: React.FC<HistoricoLotesModalProps> = ({
  isOpen,
  onClose,
  onViewDetails,
  user,
  clienteId
}) => {
  const [lotes, setLotes] = useState<DisparoLote[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('all');
  const [customDateFrom, setCustomDateFrom] = useState<Date | undefined>(undefined);
  const [customDateTo, setCustomDateTo] = useState<Date | undefined>(undefined);
  const [isFromCalendarOpen, setIsFromCalendarOpen] = useState(false);
  const [isToCalendarOpen, setIsToCalendarOpen] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedLoteIds, setSelectedLoteIds] = useState<number[]>([]);
  const [selectionMode, setSelectionMode] = useState(false);

  const statusOptions = [
    { value: 'all', label: 'Todos os Status' },
    { value: 'PENDENTE', label: 'Pendente' },
    { value: 'FILA', label: 'Fila' },
    { value: 'AGUARDANDO_CONFIRMACAO', label: 'Aguardando Confirmação' },
    { value: 'ENVIADO', label: 'Enviado' },
    { value: 'FALHA', label: 'Falha' },
    { value: 'CONTATO_INVALIDO', label: 'Contato Inválido' },
    { value: 'BAIXADO', label: 'Baixado' }
  ];

  const periodOptions = [
    { value: 'all', label: 'Todos os Períodos' },
    { value: 'this_month', label: 'Este Mês' },
    { value: 'last_month', label: 'Mês Passado' },
    { value: 'last_3_months', label: 'Últimos 3 Meses' },
    { value: 'last_6_months', label: 'Últimos 6 Meses' },
    { value: 'this_year', label: 'Este Ano' },
    { value: 'last_year', label: 'Ano Passado' },
    { value: 'custom', label: 'Período Personalizado' }
  ];

  const loadLotes = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const params: any = {};
      
      // Filtro por cliente
      if (user?.role === 'admin' && clienteId) {
        params.client_id = clienteId;
      } else if (user?.role === 'cliente' && user.client_id) {
        params.client_id = user.client_id;
      }

      // Filtro por status
      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }

      // Filtro por período
      if (periodFilter && periodFilter !== 'all') {
        const now = new Date();
        let dateFrom: string;
        let dateTo: string;

        switch (periodFilter) {
          case 'this_month':
            dateFrom = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
            dateTo = now.toISOString().split('T')[0];
            break;
          case 'last_month':
            const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            dateFrom = lastMonth.toISOString().split('T')[0];
            dateTo = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0];
            break;
          case 'last_3_months':
            dateFrom = new Date(now.getFullYear(), now.getMonth() - 3, 1).toISOString().split('T')[0];
            dateTo = now.toISOString().split('T')[0];
            break;
          case 'last_6_months':
            dateFrom = new Date(now.getFullYear(), now.getMonth() - 6, 1).toISOString().split('T')[0];
            dateTo = now.toISOString().split('T')[0];
            break;
          case 'this_year':
            dateFrom = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
            dateTo = now.toISOString().split('T')[0];
            break;
          case 'last_year':
            dateFrom = new Date(now.getFullYear() - 1, 0, 1).toISOString().split('T')[0];
            dateTo = new Date(now.getFullYear() - 1, 11, 31).toISOString().split('T')[0];
            break;
          case 'custom':
            if (customDateFrom && customDateTo) {
              dateFrom = customDateFrom.toISOString().split('T')[0];
              dateTo = customDateTo.toISOString().split('T')[0];
            } else {
              return; // Não carregar se período personalizado não estiver definido
            }
            break;
          default:
            return;
        }

        params.date_from = dateFrom;
        params.date_to = dateTo;
      }

      // Filtro por termo de busca
      if (searchTerm) {
        params.search = searchTerm;
      }

      console.log('🔍 [HistoricoLotesModal] Carregando lotes com parâmetros:', params);
      const response = await api.get('/disparos/lotes', { params });
      
      if (response && response.lotes) {
        setLotes(response.lotes);
        console.log(`✅ [HistoricoLotesModal] ${response.lotes.length} lotes carregados`);
      } else {
        setLotes([]);
        console.log('⚠️ [HistoricoLotesModal] Nenhum lote encontrado');
      }
    } catch (error) {
      console.error('❌ [HistoricoLotesModal] Erro ao carregar lotes:', error);
      setLotes([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadLotes();
    } else {
      // Fechar calendários quando o modal for fechado
      setIsFromCalendarOpen(false);
      setIsToCalendarOpen(false);
    }
  }, [isOpen, statusFilter, periodFilter, customDateFrom, customDateTo, searchTerm]);

  // Reset calendários quando período não for custom
  useEffect(() => {
    if (periodFilter !== 'custom') {
      setIsFromCalendarOpen(false);
      setIsToCalendarOpen(false);
    }
  }, [periodFilter]);

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'PENDENTE': { label: 'Pendente', variant: 'secondary' as const },
      'FILA': { label: 'Fila', variant: 'default' as const },
      'AGUARDANDO_CONFIRMACAO': { label: 'Aguardando Confirmação', variant: 'secondary' as const },
      'ENVIADO': { label: 'Enviado', variant: 'default' as const },
      'FALHA': { label: 'Falha', variant: 'destructive' as const },
      'CONTATO_INVALIDO': { label: 'Contato Inválido', variant: 'destructive' as const },
      'BAIXADO': { label: 'Baixado', variant: 'secondary' as const },
      'CONCLUIDO': { label: 'Concluído', variant: 'default' as const },
      'PAUSADO': { label: 'Pausado', variant: 'outline' as const }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || { label: status, variant: 'secondary' as const };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getTipoEnvioBadge = (tipo: string) => {
    const tipoConfig = {
      'LEMBRETE': { label: 'LEMBRETE', variant: 'secondary' as const },
      'COBRANCA': { label: 'COBRANCA', variant: 'destructive' as const },
      'REVISTORIA': { label: 'REVISTORIA', variant: 'default' as const },
      'COBRANCA_PERSONALIZADA': { label: 'COBRANÇA_PERSONALIZADA', variant: 'destructive' as const }
    };

    const config = tipoConfig[tipo as keyof typeof tipoConfig] || { label: tipo, variant: 'secondary' as const };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5" />
                Histórico de Lotes
              </DialogTitle>
              <DialogDescription>
                Visualize e filtre lotes de disparos por período e status
              </DialogDescription>
            </div>
            <Button
              variant="outline"
              onClick={() => setShowExportModal(true)}
              className="flex items-center gap-2 mr-8"
            >
              <Download className="h-4 w-4" />
              Exportar Lotes
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {/* Filtros */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="space-y-2">
              <Label htmlFor="search">Buscar</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="search"
                  placeholder="Nome do lote..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o status" />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="period">Período</Label>
              <Select value={periodFilter} onValueChange={setPeriodFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o período" />
                </SelectTrigger>
                <SelectContent>
                  {periodOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {periodFilter === 'custom' && (
              <div className="space-y-2">
                <Label>Período Personalizado</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="dateFrom" className="text-xs">De:</Label>
                    <Popover open={isFromCalendarOpen} onOpenChange={setIsFromCalendarOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="h-11 justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {customDateFrom ? format(customDateFrom, 'dd/MM/yyyy') : 'Data início'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-white border shadow-lg z-50" align="start" side="bottom">
                        <Calendar
                          mode="single"
                          selected={customDateFrom}
                          onSelect={(date) => {
                            setCustomDateFrom(date);
                            setIsFromCalendarOpen(false);
                          }}
                          locale={ptBR}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label htmlFor="dateTo" className="text-xs">Até:</Label>
                    <Popover open={isToCalendarOpen} onOpenChange={setIsToCalendarOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="h-11 justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {customDateTo ? format(customDateTo, 'dd/MM/yyyy') : 'Data fim'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-white border shadow-lg z-50" align="start" side="bottom">
                        <Calendar
                          mode="single"
                          selected={customDateTo}
                          onSelect={(date) => {
                            setCustomDateTo(date);
                            setIsToCalendarOpen(false);
                          }}
                          locale={ptBR}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Tabela de Lotes */}
          <div className="border rounded-lg overflow-hidden">
            <div className="max-h-[60vh] overflow-auto">
              <div className="min-w-full overflow-x-auto">
                <Table>
                <TableHeader>
                  <TableRow>
                    {selectionMode && (
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedLoteIds.length === lotes.length && lotes.length > 0}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedLoteIds(lotes.map(l => l.id));
                            } else {
                              setSelectedLoteIds([]);
                            }
                          }}
                        />
                      </TableHead>
                    )}
                    <TableHead>Nome do Lote</TableHead>
                    <TableHead>Tipo de Envio</TableHead>
                    <TableHead>Tag</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Enviados</TableHead>
                    <TableHead>Aguard. Conf.</TableHead>
                    <TableHead>Fila</TableHead>
                    <TableHead>Pendentes</TableHead>
                    <TableHead>Inválidos</TableHead>
                    <TableHead>Falhas</TableHead>
                    <TableHead>Baixados</TableHead>
                    <TableHead>Valor Total</TableHead>
                    <TableHead>Criado em</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={selectionMode ? 15 : 14} className="text-center py-8">
                        <div className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                          Carregando lotes...
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : lotes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={selectionMode ? 15 : 14} className="text-center py-8 text-gray-500">
                        Nenhum lote encontrado com os filtros aplicados
                      </TableCell>
                    </TableRow>
                  ) : (
                    lotes.map((lote) => (
                      <TableRow key={lote.id}>
                        {selectionMode && (
                          <TableCell>
                            <Checkbox
                              checked={selectedLoteIds.includes(lote.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedLoteIds([...selectedLoteIds, lote.id]);
                                } else {
                                  setSelectedLoteIds(selectedLoteIds.filter(id => id !== lote.id));
                                }
                              }}
                            />
                          </TableCell>
                        )}
                        <TableCell className="font-medium">
                          Lote #{lote.id}
                        </TableCell>
                        <TableCell>
                          {getTipoEnvioBadge(lote.tipo_envio)}
                        </TableCell>
                        <TableCell>
                          {lote.tag && (
                            <Badge variant="outline">{lote.tag}</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(lote.status)}
                        </TableCell>
                        <TableCell>{lote.total_boletos || 0}</TableCell>
                        <TableCell>{lote.total_enviados || 0}</TableCell>
                        <TableCell>{lote.total_aguardando_confirmacao ?? 0}</TableCell>
                        <TableCell>{lote.total_fila || 0}</TableCell>
                        <TableCell>{lote.total_pendentes || 0}</TableCell>
                        <TableCell>{lote.total_invalidos || 0}</TableCell>
                        <TableCell>{lote.total_falhas || 0}</TableCell>
                        <TableCell>{lote.total_baixados || 0}</TableCell>
                        <TableCell>{formatCurrency(lote.valor_total || 0)}</TableCell>
                        <TableCell>{formatDateBR(lote.created_at)}</TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onViewDetails(lote)}
                            className="flex items-center gap-1"
                          >
                            <Eye className="h-4 w-4" />
                            Ver Detalhes
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
                </Table>
              </div>
            </div>
          </div>

          {/* Resumo */}
          {lotes.length > 0 && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="font-medium">Total de Lotes:</span> {lotes.length}
                </div>
                <div>
                  <span className="font-medium">Total de Boletos:</span> {lotes.reduce((sum, lote) => sum + (lote.total_boletos || 0), 0)}
                </div>
                <div>
                  <span className="font-medium">Total Enviados:</span> {lotes.reduce((sum, lote) => sum + (lote.total_enviados || 0), 0)}
                </div>
                <div>
                  <span className="font-medium">Valor Total:</span> {formatCurrency(lotes.reduce((sum, lote) => sum + (lote.valor_total || 0), 0))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal de Exportação */}
        <ExportarLotesModal
          isOpen={showExportModal}
          onClose={() => {
            setShowExportModal(false);
            setSelectionMode(false);
            setSelectedLoteIds([]);
          }}
          lotes={lotes}
          user={user}
          clienteId={clienteId}
          selectedLoteIds={selectedLoteIds}
          onSelectionModeChange={setSelectionMode}
        />
      </DialogContent>
    </Dialog>
  );
};

export default HistoricoLotesModal;
